import { createApi } from '@reduxjs/toolkit/query/react';
import { getErrorMessage } from '_helpers/apiError';
import { crBaseQuery } from '_helpers/baseQuery';
import { alertActions } from '_reducers/alerts.reducer';
import {
  materialActions,
  materialPagingActions,
} from '_reducers/materials.reducer.v2';

/**
 * @public
 * @name materialApi
 * @description Creates material api definition.
 */
export const materialApi = createApi({
  baseQuery: crBaseQuery(),
  reducerPath: 'materialApi',
  endpoints: (builder) => ({
    /** @name getMaterialsRequest */
    getMaterialsRequest: builder.query({
      query: (request) => {
        const query = {
          page: request?.page ?? 1,
          accountID: request?.accountID ?? 0,
          searchTerm: request?.searchTerm ?? ''
        };

        return `/materials/paged?${new URLSearchParams(query).toString()}`;
      },
      onQueryStarted: async ({ accountID, searchTerm }, { dispatch, queryFulfilled }) => {
        /**
         * `onStart` side-effect
         * In case of material search term is account id.
         * Because we want to fetch records by account. With each fetch request only the page number will increase.
         */
        dispatch(materialPagingActions.requestPage(`${accountID}_${searchTerm ?? ''}`));

        try {
          const result = await queryFulfilled;

          /**
           * `onSuccess` side-effect
           *    - Dispatch items payload to store in entities state.
           *    - Prepare a payload to cache the paged data and dispatch.
           */
          await dispatch(materialActions.addMaterials(result.data.items));
          const params = new URL(result.meta.request.url).searchParams;

          const payload = {
            page: params.get('page'),

            /**
             * In case of material search term is account id and search name.
             */
            searchTerm: `${params.get('accountID')}_${params.get('searchTerm')}`,

            /**
             * For more reference to why only entities id's are being dispatched.
             * @see createPaginationSlice function documentation to understand the store state structure.
             */
            data: result.data.items.map((m) => m.materialID),
          };

          dispatch(materialPagingActions.addPage(payload));
        } catch (e) {
          /**
           * `onError` side-effect
           */
          dispatch(alertActions.addFailure(getErrorMessage(e)));
        }
      },
    }),
    /** @name createMaterialRequest */
    createMaterialRequest: builder.mutation({
      query: (body) => ({
        url: '/materials',
        method: 'POST',
        body,
      }),
      // trigger side effects or optimistic updates
      onQueryStarted: async (body, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;

          /**
           * `onSuccess` side-effect
           * - Invalidate the current paged data.
           * - Invalidate cached api state.
           * - Notify user of call success.
           */
          dispatch(materialPagingActions.clearPagedData());
          dispatch(materialApi.util.resetApiState()); // There should be a better alternative. May be look into invalidateTags
          dispatch(
            alertActions.addSuccess('Material created.')
          );
        } catch (e) {
          /**
           * `onError` side-effect
           */
          dispatch(alertActions.addFailure(getErrorMessage(e)));
        }
      },
    }),
    /** @name updateMaterialRequest */
    updateMaterialRequest: builder.mutation({
      query: (body) => ({
        url: `/materials/${body.materialID}`,
        method: 'PUT',
        body,
      }),
      // trigger side effects or optimistic updates
      onQueryStarted: async (body, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;

          /**
           * `onSuccess` side-effect
           * - Invalidate the current paged data.
           * - Invalidate cached api state.
           * - Notify user of call success.
           */
          dispatch(materialPagingActions.clearPagedData());
          dispatch(materialApi.util.resetApiState()); // There should be a better alternative. May be look into invalidateTags
          dispatch(
            alertActions.addSuccess('Material updated.')
          );
        } catch (e) {
          /**
           * `onError` side-effect
           */
          dispatch(alertActions.addFailure(getErrorMessage(e)));
        }
      },
    }),
    /** @name deleteMaterialRequest */
    deleteMaterialRequest: builder.mutation({
      query: (materialID) => ({
        url: `/materials/${materialID}`,
        method: 'DELETE'
      }),
      // trigger side effects or optimistic updates
      onQueryStarted: async (materialID, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;

          /**
           * `onSuccess` side-effect
           * - Invalidate the current paged data.
           * - Invalidate cached api state.
           * - Notify user of call success.
           */
          dispatch(materialPagingActions.clearPagedData());
          dispatch(materialApi.util.resetApiState()); // There should be a better alternative. May be look into invalidateTags
          dispatch(
            alertActions.addSuccess('Material deleted.')
          );
        } catch (e) {
          /**
           * `onError` side-effect
           */
          dispatch(alertActions.addFailure(getErrorMessage(e)));
        }
      },
    }),
  }),
});

/**
 * Hook to material request.
 */
export const {
  useGetMaterialsRequestQuery,
  useCreateMaterialRequestMutation,
  useUpdateMaterialRequestMutation,
  useDeleteMaterialRequestMutation
} = materialApi;
